import { AlertCircle, Package } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";

/* ----------------------------------
   KPI CARD
----------------------------------- */
const KPI = ({ title, value, subtext }) => (
  <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4">
    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
      {title}
    </p>
    <p className="text-2xl font-semibold text-gray-900">{value}</p>
    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
  </div>
);

const SkeletonKPI = () => (
  <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4 animate-pulse">
    <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
    <div className="h-8 w-32 bg-gray-200 rounded" />
  </div>
);

const SkeletonContent = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-6 w-48 bg-gray-200 rounded" />
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="h-12 bg-gray-100 rounded" />
    ))}
  </div>
);

/* ----------------------------------
   INVENTORY REPORTS (LIVE)
----------------------------------- */
const InventoryReports = () => {
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/reports/inventory`,
      );

      setInventoryData({
        kpis: res.data.kpis || [],
        lowStock: res.data.lowStock || [],
        topUsed: res.data.topUsed || [],
      });
    } catch (err) {
      console.error("Failed to fetch inventory data", err);
      setInventoryData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryData();
  }, []);

  return (
    <div className="space-y-6">
      {/* KPI GRID */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
          : inventoryData?.kpis?.map((kpi, idx) => <KPI key={idx} {...kpi} />)}
      </div>

      {/* CONTENT */}
      <div className="bg-white border border-[#F7BFAD] rounded-2xl p-6 space-y-8">
        {loading ? (
          <SkeletonContent />
        ) : !inventoryData ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-400">
            No inventory data available
          </div>
        ) : (
          <>
            {/* LOW STOCK ALERTS */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <AlertCircle className="size-5 text-red-500" />
                Low Stock Alerts
              </h3>

              {inventoryData.lowStock.length ? (
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {inventoryData.lowStock.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        item.status === "critical"
                          ? "bg-red-50 border-red-200"
                          : "bg-yellow-50 border-yellow-200"
                      }`}
                    >
                      <p className="text-sm font-medium">{item.product}</p>
                      <div className="text-right">
                        <p
                          className={`text-sm font-bold ${
                            item.status === "critical"
                              ? "text-red-600"
                              : "text-yellow-600"
                          }`}
                        >
                          {item.current} units
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {item.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No low stock items 🎉</p>
              )}
            </div>

            {/* TOP USED PRODUCTS */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Package className="size-5 text-[#f0653a]" />
                Most Used Products
              </h3>

              {inventoryData.topUsed.length ? (
                <div className="space-y-2">
                  {inventoryData.topUsed.map((product, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="text-lg font-bold text-gray-300 w-8">
                        #{idx + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{product.product}</p>
                        <p className="text-xs text-gray-500">
                          {product.category}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-[#f0653a]">
                        {product.usage} units
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No usage data available</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InventoryReports;
